import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetConfigurationQuery, useGetMenuQuery } from '@/app/api/resourceApi';
import { formatPrice } from '@/components/ui/Price';
import { formatTicketTime } from '@/features/waiter/ticketTime';
import {
  groupsForMode,
  groupsForAmount,
  selectionsForGroups,
  cartOptionsTotalCents,
  unansweredRequiredGroups,
  selectedCount,
  toWireCartOptions,
} from '@/features/checkout/cartOptions';
import { selectPromotion, eligibleDiscountSubtotal } from '@/features/checkout/selectPromotion';
import { optionEntries } from '@/features/checkout/ordersApi';
import { describeQuoteRefusal } from '@/features/checkout/quoteRefusal';
import { repriceCartLines } from '@/features/cart/repriceCartLines';
import {
  selectWaiterCartLines,
  selectWaiterCartTotal,
  clearWaiterCartSession,
  setWaiterLineQty,
  updateWaiterLineUnitPrice,
  markWaiterSendPending,
} from '@/features/waiter/waiterCartSlice';
import { usePostRoundMutation, useGetSessionDetailQuery, waiterApi } from '@/features/waiter/waiterApi';
import { clearWaiterAuth } from '@/features/waiter/waiterSlice';
import { useModalBackClose } from './useModalBackClose';
import {
  heldRoundsOf,
  planIsTrivial,
  planSendByCourse,
  splitCartByCourse,
  unclassifiedLines,
} from './commandesGrouping';
import { diagnoseWaiterCart, staleLineMessage, type StaleCartLine } from './staleCart';
import type { ShowWaiterToast } from './waiterToast';
import type { OptionGroup, SelectedOption } from '@/types';

export interface TicketConfirmRoundProps {
  sessionUuid: string;
  restaurantUuid: string;
  kind: 'dine_in' | 'takeaway';
  /** DF-0557 — what the ticket IS (`activeTicket.kind`), as opposed to
   *  `kind`, the round's sale mode: a table switched to « Emporter » still
   *  sends to that table. Defaults to `kind`. */
  ticketKind?: 'dine_in' | 'takeaway';
  /** DF-0557 — the table label (« 15+16 ») or the note's name, named in the
   *  dialog's title: a waiter dropped back on the last table served (restored
   *  tab, return from background) sent another table's round to it without
   *  seeing where it went. */
  ticketLabel: string;
  /** SC-902 — design §1.7: `sessionDetail.session.mode` from `TicketPage`.
   *  A buffet dine-in session never offers the course split (spec §1.9);
   *  `undefined` (an older caller, or a takeaway note which has no `mode`)
   *  behaves like anything other than `'buffet'` — harmless since `kind` on
   *  its own already excludes takeaway from the feature. */
  sessionMode?: 'ordering' | 'buffet';
  /** The NOTE's guest token (spec §1.7 D2/§2) — the round is authenticated
   *  like a customer round, not with the waiter JWT (never sent here). */
  guestToken: string;
  /** SC-902 — the staff waiter JWT, distinct from `guestToken`: needed here
   *  only to read the session's live held rounds via
   *  `useGetSessionDetailQuery({sessionUuid, token})` — the exact same query
   *  `TicketCommandes`/`TicketFooter` already subscribe to, so this is a
   *  shared RTK Query cache entry, not an extra request. */
  token: string;
  onClose: () => void;
  /** 201 — cart cleared, caller switches to Commandes (W-T-52). */
  onConfirmed: () => void;
  /** DF-0106 — « Compléter » on a line the fresh menu now refuses (a
   *  required group added mid-service): opens the options sheet for THAT
   *  line. TicketPage closes this dialog first — the options sheet sits at
   *  z-1150, under this one's z-1180. */
  onEditLine: (lineId: string) => void;
  showToast: ShowWaiterToast;
}

/** Two colours cannot both be "checked" and "blocked" at once — mirrors
 *  `CartOptionsSection.tsx`'s (unexported) `toggle`, reimplemented here on
 *  local component state instead of the redux-persisted `checkoutSlice`: a
 *  round's cart-question answers are per-confirm-attempt, never restored
 *  across a reopen (the checkout's persistence exists for an abandoned
 *  multi-step form, which the ticket's single small dialog is not). */
function toggleCartOptionAnswer(
  answers: SelectedOption[],
  group: OptionGroup,
  optionId: string,
): SelectedOption[] {
  const current = answers.find((s) => s.groupId === group.id);
  const chosen = current?.options ?? [];
  const max = group.max ?? 1;
  let nextOptions: { id: string; qty: number }[];
  if (chosen.some((o) => o.id === optionId)) {
    nextOptions = chosen.filter((o) => o.id !== optionId);
  } else if (max <= 1) {
    nextOptions = [{ id: optionId, qty: 1 }];
  } else if (selectedCount(current) >= max) {
    return answers;
  } else {
    nextOptions = [...chosen, { id: optionId, qty: 1 }];
  }
  const rest = answers.filter((s) => s.groupId !== group.id);
  return nextOptions.length ? [...rest, { groupId: group.id, options: nextOptions }] : rest;
}

function centsToEuros(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** DF-0120 — `resourceApi.ts`'s `fetchBaseQuery` carries no
 *  timeout and `getMenu` is three paginated requests: on a stalled LAN the
 *  send-time refetch below could hang for tens of seconds with the round
 *  trapped behind it. Bounded so a slow network degrades to "send on the
 *  menu already in hand", never to a stuck Confirmer. */
const SEND_TIME_REFETCH_TIMEOUT_MS = 4000;

/**
 * 04-ux-mobile-spec.md §2.5, 06-test-cases.md W-T-49..58 — « Voulez-vous
 * confirmer le panier ? ». Sends the round with the NOTE's guest token
 * (never the waiter JWT), then merges the response order into the
 * `SessionDetail` cache immediately (W-T-58) on top of the `invalidatesTags`
 * refetch `postRound` already fires. Cart-level questions (SC-739/SC-748,
 * `menu.cartOptionGroups`) are asked here, reusing
 * `@/features/checkout/cartOptions`'s pure helpers — never
 * `CartOptionsSection.tsx` itself, which is wired to the checkout's numbered
 * `CheckoutStep`/`StepCard` machinery that has no equivalent in this dialog.
 */
export default function TicketConfirmRound({
  sessionUuid,
  restaurantUuid,
  kind,
  ticketKind = kind,
  ticketLabel,
  sessionMode,
  guestToken,
  token,
  onClose,
  onConfirmed,
  onEditLine,
  showToast,
}: TicketConfirmRoundProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cartLines = useAppSelector(selectWaiterCartLines(sessionUuid));
  const cartTotalCents = useAppSelector(selectWaiterCartTotal(sessionUuid));
  // DF-0106 — a tablet open since the start of the service was ordering on
  // the catalogue of hours ago: `getMenu`'s tag is never invalidated and the
  // ticket never refetched, so a required group created mid-service was
  // unknown here, the line left without an answer and the quote refused the
  // round (9 × 400 in 50 s at Yuji, 2026-09-02). Same two guards as the
  // guest's CartDrawer: refetch on open, refetch again right before sending.
  const { data: menu, refetch: refetchMenu } = useGetMenuQuery(restaurantUuid, {
    skip: !restaurantUuid,
    refetchOnMountOrArgChange: true,
  });
  // SC-902 — design §1.7: re-read on open like the menu above, so a config
  // flip mid-service (the option switched off/on, or a course renamed) is
  // never judged against a stale snapshot.
  const { data: restaurantConfig, refetch: refetchConfig } = useGetConfigurationQuery(restaurantUuid, {
    skip: !restaurantUuid,
    refetchOnMountOrArgChange: true,
  });
  // SC-902 — design §1.10 rule A: the SAME query TicketPage/TicketCommandes/
  // TicketFooter already run (identical args ⇒ one shared RTK Query cache
  // entry), read here only for the session's live held rounds.
  const { data: sessionDetail } = useGetSessionDetailQuery(
    { sessionUuid, token },
    { skip: !token || !sessionUuid },
  );
  const [postRound] = usePostRoundMutation();

  // SC-902 — design §1.2/§1.7: resolved with the same "absent ⇒ off"
  // defaults `transformConfiguration` already guarantees, so an older
  // server or a config still loading behaves exactly like the option off.
  const dineInCoursesEnabled = restaurantConfig?.dineInCoursesEnabled ?? false;
  const dineInCourses = restaurantConfig?.dineInCourses ?? [];
  const coursesEligible =
    kind === 'dine_in' && sessionMode !== 'buffet' && dineInCoursesEnabled && dineInCourses.length > 0;
  const categoryIdByProduct = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of menu?.items ?? []) map.set(item.id, item.categoryId);
    return map;
  }, [menu]);
  // SC-1614 — lines no service claims never wait for a service: they are
  // planned apart (`unclassified`) and always go out now.
  const looseLines = useMemo(
    () => (coursesEligible ? unclassifiedLines(cartLines, dineInCourses, categoryIdByProduct) : []),
    [coursesEligible, cartLines, dineInCourses, categoryIdByProduct],
  );
  const courseGroups = useMemo(() => {
    if (!coursesEligible) return [];
    const classified = cartLines.filter((line) => !looseLines.includes(line));
    return classified.length > 0
      ? splitCartByCourse(classified, dineInCourses, categoryIdByProduct)
      : [];
  }, [coursesEligible, cartLines, looseLines, dineInCourses, categoryIdByProduct]);
  const liveHeld = useMemo(
    () => (coursesEligible ? heldRoundsOf(sessionDetail?.orders ?? [], dineInCourses) : []),
    [coursesEligible, sessionDetail?.orders, dineInCourses],
  );
  const coursePlan = useMemo(
    () =>
      coursesEligible
        ? planSendByCourse({ groups: courseGroups, heldRounds: liveHeld, unclassified: looseLines })
        : [],
    [coursesEligible, courseGroups, liveHeld, looseLines],
  );
  // spec §1.10 rule A — the choice appears whenever the plan differs from
  // "everything now", even for a single service joining a held round.
  const showCourseChoice = coursesEligible && !planIsTrivial(coursePlan);
  const [sendMode, setSendMode] = useState<'byCourse' | 'all'>('byCourse');
  const sendByCourse = showCourseChoice && sendMode === 'byCourse';

  const [cartOptionAnswers, setCartOptionAnswers] = useState<SelectedOption[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  // Plafonds 20 plats par ligne / 60 par tournee : une table de 30 les
  // depasse. Le refus ouvre un champ PIN du personnel ; le PIN renvoye avec
  // la tournee leve les plafonds cote serveur (sale_api `staff_pin`).
  const [needsStaffPin, setNeedsStaffPin] = useState(false);
  const [staffPin, setStaffPin] = useState('');
  // Synchronous guard (W-T-53) — `postRound`'s own `isLoading` only updates on
  // the next render, which a same-tick double click can race; this flag is
  // set before the first `await`.
  const [submitting, setSubmitting] = useState(false);
  // The send-time refetch is awaited (bounded above, but still awaited) — if
  // Annuler or the hardware back button closes the dialog while that await
  // is in flight, the round must never go out. Set on unmount AND directly
  // by `handleClose`, so the check inside `handleConfirm` sees it on the
  // very next line even if React hasn't unmounted yet.
  const cancelledRef = useRef(false);
  useEffect(() => {
    // StrictMode (dev) runs this cleanup once right after mount and then
    // re-runs the effect: without the reset the flag stayed true, so every
    // Confirmer returned silently and « Envoi… » never cleared.
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  function handleClose() {
    cancelledRef.current = true;
    onClose();
  }

  useModalBackClose('confirm-modal', handleClose);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  // SC-739/SC-748/SC-753 — dine-in reads its own sale mode ('sur_place'), a
  // takeaway note reads 'takeaway' → `saleModeOf` resolves 'emporter'. Never
  // `checkout.mode`, which the ticket never touches (memory
  // `site_commande_checkout_mode_never_says_dine_in`).
  const cartOptionGroupsForMode = useMemo(
    () => groupsForMode(menu?.cartOptionGroups, kind),
    [menu?.cartOptionGroups, kind],
  );
  const cartOptionGroups = useMemo(
    () => groupsForAmount(cartOptionGroupsForMode, cartTotalCents),
    [cartOptionGroupsForMode, cartTotalCents],
  );
  const answeredCartOptions = useMemo(
    () => selectionsForGroups(cartOptionGroups, cartOptionAnswers),
    [cartOptionGroups, cartOptionAnswers],
  );
  const cartOptionsSupplement = useMemo(
    () => cartOptionsTotalCents(cartOptionGroups, answeredCartOptions),
    [cartOptionGroups, answeredCartOptions],
  );
  const missingCartOptions = useMemo(
    () => unansweredRequiredGroups(cartOptionGroups, answeredCartOptions),
    [cartOptionGroups, answeredCartOptions],
  );

  // SC-808 — a takeaway note gets the restaurant's promotion, exactly like the
  // consumer take-out order the note is the counter twin of; a table round
  // never does (its quote runs in `sur_place` mode, which carries no
  // promotion). Same rule and same base as the checkout: `cartTotalCents` is
  // the dishes only — the cart-question supplement rides ON TOP of the
  // discount, it is never part of what the promotion looks at.
  //
  // The pick has to agree with `_quote_order`'s to the cent or the round is
  // refused with `total_mismatch`, hence `selectPromotion` rather than a local
  // reimplementation: it is the mirror of resource_api's `select_promotion`.
  // DF-0212 — le pourcentage ne porte que sur les lignes dont le produit ET la
  // catégorie acceptent la remise ; le seuil continue de lire le panier entier.
  const promoMatch = useMemo(
    () =>
      kind === 'takeaway'
        ? selectPromotion(
            restaurantConfig?.promotions ?? [],
            cartTotalCents,
            'emporter',
            eligibleDiscountSubtotal(cartLines, menu),
          )
        : null,
    [kind, restaurantConfig?.promotions, cartTotalCents, cartLines, menu],
  );
  const discountCents = promoMatch?.discountAmountCents ?? 0;
  const totalCents = cartTotalCents - discountCents + cartOptionsSupplement;

  // SC-902 — design §1.3/§1.10: `coursePlan`'s `course` is the SERVICE's own
  // position (display only); the wire `course` a request sends is a pure
  // ordering key over the rounds it actually creates — renumbered here
  // sequentially over the `now`/`hold` entries ONLY (a `join` entry attaches
  // to an existing round via `held_round`, it creates nothing to number).
  const { wireCourseByEntryIndex, courseLabelsWire, entryIndexByLineId } = useMemo(() => {
    const byEntryIndex = new Map<number, number>();
    const labels: Record<string, string> = {};
    let wireCourse = 0;
    coursePlan.forEach((entry, index) => {
      if (entry.kind === 'join') return;
      wireCourse += 1;
      byEntryIndex.set(index, wireCourse);
      if (entry.label) labels[String(wireCourse)] = entry.label;
    });
    const byLineId = new Map<string, number>();
    coursePlan.forEach((entry, index) => {
      for (const line of entry.lines) byLineId.set(line.lineId, index);
    });
    return { wireCourseByEntryIndex: byEntryIndex, courseLabelsWire: labels, entryIndexByLineId: byLineId };
  }, [coursePlan]);
  const firstNowEntry = coursePlan.find((entry) => entry.kind === 'now');
  // SC-1614 — the unclassified-only `now` entry has no service label.
  const entryName = (entry: { label: string }) =>
    entry.label || t('waiter.ticket.confirm.plan.unclassified');
  // Reviewer finding F1 — the server fires the LOWEST `course` group on a
  // request; that's correct whenever a `now` entry exists (it always gets
  // the lowest number above). With no `now` entry at all — every group in
  // the plan is `hold`/`join` (spec §1.10 rule A: a service added after
  // every service already held) — the lowest `hold` group would still be
  // numbered 1 and would still fire unless the request says otherwise. This
  // is exactly the request-level `hold_all: true` (spec §1.10 « troisième
  // forme »): every `course`-bearing round the request creates stays held.
  const holdAllWire = coursePlan.some((entry) => entry.kind === 'hold') && !firstNowEntry;

  // DF-0106 — lines the menu, as loaded NOW, refuses as saved. Rendered as a
  // list the waiter can act on (Compléter / Retirer) instead of a 400.
  const staleLines = useMemo(() => diagnoseWaiterCart(cartLines, menu, kind), [cartLines, menu, kind]);
  const staleText = (stale: StaleCartLine) => {
    const [key, params] = staleLineMessage(stale);
    return t(key, params);
  };

  /** DF-0120 — the send-time refetch, bounded and never-throwing.
   *  Resolves with `menu` (the cached snapshot) on either fallback path —
   *  a stalled LAN past SEND_TIME_REFETCH_TIMEOUT_MS, a rejected refetch
   *  (offline, resource_api down), or `refetchMenu` itself throwing
   *  synchronously — warning on the way out so the fallback is visible in
   *  the console rather than silent. Whichever settles first cancels the
   *  timer, so a fast refetch never leaves a dangling 4s timeout behind.
   */
  async function refetchMenuBeforeSend(): Promise<typeof menu> {
    return new Promise((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        console.warn('[waiter] send-time menu refetch timed out, sending on the cached menu');
        resolve(menu);
      }, SEND_TIME_REFETCH_TIMEOUT_MS);
      const settle = (value: typeof menu) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };
      try {
        refetchMenu()
          .unwrap()
          .then((result) => settle(result ?? menu))
          .catch(() => {
            console.warn('[waiter] send-time menu refetch failed, sending on the cached menu');
            settle(menu);
          });
      } catch {
        console.warn('[waiter] send-time menu refetch failed, sending on the cached menu');
        settle(menu);
      }
    });
  }

  async function handleConfirm() {
    if (submitting) return;
    if (missingCartOptions.length > 0) {
      setErrorText(t('checkout.cartOptions.requiredMissing'));
      return;
    }
    if (needsStaffPin && staffPin === '') {
      setErrorText(t('waiter.ticket.confirm.errors.qtePinRequired'));
      return;
    }
    setSubmitting(true);
    setErrorText(null);
    // DF-0383 — dès cet appui, ces plats ne sont plus proposés à un autre
    // onglet : pendant la relecture de la carte ci-dessous il pourrait les
    // reprendre, et cet envoi-ci partirait quand même avec eux.
    markWaiterSendPending(sessionUuid, true);
    // Declared here, not inside the try, so the refusal parser in `catch`
    // below reads the SAME snapshot the round was diagnosed and priced
    // against — never the stale one this dialog opened on.
    let fresh = menu;
    try {
      // DF-0106 — the last word is the menu as it is NOW, not the one this
      // dialog opened on. `refetchMenuBeforeSend` is bounded and
      // never-throwing: a stalled LAN or a refetch that fails outright
      // (offline, resource_api down) must not strand the waiter behind a
      // spinning Confirmer — both fall through to the menu already in hand,
      // the quote still stands behind it.
      fresh = await refetchMenuBeforeSend();
      // Annuler / the hardware back button fired while the above was in
      // flight — the round must never go out for a dialog the waiter has
      // already left.
      if (cancelledRef.current) {
        markWaiterSendPending(sessionUuid, false);
        return;
      }
      if (diagnoseWaiterCart(cartLines, fresh, kind).length > 0) {
        markWaiterSendPending(sessionUuid, false);
        setErrorText(t('waiter.ticket.stale.blocked'));
        return;
      }

      // DF-0120 — a dish's price moved since the line was added (back-office
      // edit mid-service): reprice from the fresh menu so the total sent
      // matches what the server will compute, rather than leaning on the
      // `total_mismatch` message below to explain a refusal a correct total
      // would have avoided. Buffet-included lines are left alone — their
      // frozen 0 price is the buffet formula, not a stale catalogue price,
      // and this dialog has no `isBuffetDineIn` to recompute it with.
      const freshItemsById = new Map((fresh?.items ?? []).map((item) => [item.id, item]));
      const repriced = repriceCartLines(cartLines, fresh, false, kind).filter((r) => {
        const line = cartLines.find((l) => l.lineId === r.lineId);
        return !freshItemsById.get(line?.itemId ?? '')?.includedInBuffet;
      });
      for (const line of repriced) {
        dispatch(updateWaiterLineUnitPrice({ sessionUuid, lineId: line.lineId, unitPrice: line.unitPrice }));
      }
      const repricedById = new Map(repriced.map((r) => [r.lineId, r.unitPrice]));
      const sendCartTotalCents = cartLines.reduce(
        (acc, line) => acc + (repricedById.get(line.lineId) ?? line.unitPrice) * line.qty,
        0,
      );
      const sendEligibleCents = eligibleDiscountSubtotal(
        cartLines.map((line) => ({
          itemId: line.itemId,
          unitPrice: repricedById.get(line.lineId) ?? line.unitPrice,
          qty: line.qty,
        })),
        fresh,
      );
      const sendPromoMatch =
        kind === 'takeaway'
          ? selectPromotion(
              restaurantConfig?.promotions ?? [],
              sendCartTotalCents,
              'emporter',
              sendEligibleCents,
            )
          : null;
      const sendTotalCents =
        sendCartTotalCents - (sendPromoMatch?.discountAmountCents ?? 0) + cartOptionsSupplement;

      // The 201 body's items are `OrderOutputSerializer`'s raw model fields
      // (`unit_ttc_origin`, `product_name`, …), NOT the `sessions/<uuid>/`
      // detail shape — `postRound` deliberately does not transform them into
      // a `WaiterOrder` (review finding, F45 follow-up). W-T-58's "without
      // waiting for a poll" is satisfied by `postRound`'s own
      // `invalidatesTags`, which fires an immediate refetch of
      // `sessions/<uuid>/` — NOT the 5s poll — the moment this resolves.
      await postRound({
        sessionUuid,
        restaurantUuid,
        guestToken,
        type: kind === 'dine_in' ? 'dine_in' : 'take_out',
        lines: cartLines.map((line) => {
          const base = {
            product_uuid: line.itemId,
            quantity: line.qty,
            options: optionEntries(line),
            comment: line.note ?? '',
          };
          // SC-902 — design §1.3/§1.10: « Tout en même temps » (or the choice
          // never having appeared at all) sends exactly today's grammar —
          // no `course`, no `held_round`, byte-identical to before this
          // feature existed.
          if (!sendByCourse) return base;
          const entryIndex = entryIndexByLineId.get(line.lineId);
          const entry = entryIndex != null ? coursePlan[entryIndex] : undefined;
          if (!entry) return base;
          if (entry.kind === 'join') return { ...base, held_round: entry.round };
          return { ...base, course: wireCourseByEntryIndex.get(entryIndex as number) };
        }),
        totalTtc: centsToEuros(sendTotalCents),
        cartOptions: toWireCartOptions(answeredCartOptions),
        ...(sendByCourse
          ? { courseLabels: courseLabelsWire, ...(holdAllWire ? { holdAll: true } : {}) }
          : {}),
        ...(needsStaffPin && staffPin ? { staffPin } : {}),
      }).unwrap();

      dispatch(clearWaiterCartSession(sessionUuid));
      onConfirmed();
    } catch (err) {
      const payload = err as { status?: number | string; data?: { code?: string; detail?: unknown; seconds_remaining?: number } };
      const code = payload?.data?.code ?? (typeof payload?.data?.detail === 'string' ? payload.data.detail : undefined);

      if (code === 'WAITER_REVOKED' || code === 'WAITER_MODE_DISABLED') {
        dispatch(clearWaiterAuth(restaurantUuid));
        return;
      }
      if (code === 'SESSION_NOT_OPEN') {
        // TicketPage's `sessionDetail` effect is the SINGLE place that reacts
        // to a gone session (toast + navigate) — invalidate and let it fire
        // once the refetch confirms the session is no longer open, rather
        // than duplicating that navigation here.
        showToast(t('waiter.ticket.closedElsewhere'));
        dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
        onClose();
        return;
      }
      if (code === 'ALREADY_HANDED_OFF') {
        showToast(t('waiter.ticket.confirm.errors.alreadyHandedOff'));
        dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
        onClose();
        return;
      }
      if (code === 'online_ordering_disabled') {
        setErrorText(t('waiter.ticket.confirm.errors.onlineOrderingDisabled'));
        return;
      }
      if (code === 'ROUND_COOLDOWN') {
        const seconds = payload?.data?.seconds_remaining ?? 0;
        setCooldown(seconds);
        setErrorText(t('checkout.errors.roundCooldown', { seconds }));
        return;
      }
      if (code === 'LINE_QTE_EXCEEDED' || code === 'ORDER_QTE_EXCEEDED') {
        setNeedsStaffPin(true);
        setErrorText(
          `${t(code === 'LINE_QTE_EXCEEDED' ? 'waiter.ticket.confirm.errors.lineQteExceeded' : 'waiter.ticket.confirm.errors.orderQteExceeded')} ${t('waiter.ticket.confirm.errors.qtePinRequired')}`,
        );
        return;
      }
      // DF-0253 — le carnet ne peut pas deviner que sa clé d'envoi est morte :
      // ce code tombait sur « Une erreur est survenue », et le serveur
      // réessayait indéfiniment (17 fois chez Yuji le 2026-09-18). La clé est
      // désormais tournée sur tout refus 4xx (`waiterApi.ts`), donc ce cas ne
      // devrait plus arriver ; s'il arrive quand même — deux appareils sur la
      // même table, un serveur ancien — le message dit le geste qui débloque.
      //
      // SC-1572 — ce 409 veut surtout dire autre chose : le 1er envoi a dépassé
      // le délai MAIS la tournée est arrivée ; le serveur a ensuite modifié le
      // panier et renvoyé sous la même clé. `postRound` garde alors la clé (la
      // changer renverrait la tournée déjà arrivée : papier et addition en
      // double). On relit la session pour que la tournée arrivée s'affiche, on
      // garde le panier (rien ne dit de façon sûre quels plats sont déjà
      // partis) et on le dit au serveur.
      if (code === 'IDEMPOTENCY_CONFLICT') {
        dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
        setErrorText(t('waiter.ticket.confirm.errors.idempotencyConflict'));
        return;
      }
      if (code === 'BAD_PIN') {
        setStaffPin('');
        setErrorText(t('waiter.pinPad.errorBadPin'));
        return;
      }
      if (code === 'PIN_LOCKED' || code === 'PIN_LOCKED_RESTAURANT') {
        setStaffPin('');
        setErrorText(t('waiter.pinPad.errorLocked'));
        return;
      }
      // SC-902 — design §1.3: the option was switched off mid-open-sheet.
      // The sheet stays open, but falls back to the plain layout as soon as
      // `refetchConfig` lands (`coursesEligible` recomputes to false).
      if (code === 'COURSES_DISABLED') {
        showToast(t('waiter.ticket.confirm.errors.coursesDisabled'));
        refetchConfig();
        return;
      }
      // SC-902 — design §1.10 rule B2: the anchor is `pending_validation`
      // (a guest QR round to confirm/refuse first) — splitting into more
      // rounds now would let a later refusal wipe out the waiter's split.
      // « Tout en même temps » is still allowed on a pending anchor (today's
      // path), so pre-selecting it gives the waiter a one-tap way forward.
      if (code === 'COURSES_PENDING_ANCHOR') {
        showToast(t('waiter.ticket.confirm.errors.coursesPendingAnchor'));
        setSendMode('all');
        return;
      }
      // SC-902 — design §1.10 rule A: the held round this line meant to join
      // was fired to the kitchen between this sheet opening and Confirmer.
      // Invalidating refreshes `sessionDetail.orders`, which recomputes
      // `liveHeld`/`coursePlan` on the next render.
      if (code === 'HELD_ROUND_UNKNOWN') {
        showToast(t('waiter.ticket.confirm.errors.heldRoundUnknown'));
        dispatch(waiterApi.util.invalidateTags([{ type: 'SessionDetail', id: sessionUuid }]));
        return;
      }
      // DF-0107 — le motif exact du refus voyage jusqu'ici dans
      // `data.quote.detail` (un objet IMBRIQUÉ, que ni `data.code` ni
      // `data.detail` ne voient) et n'était lu nulle part dans le dépôt. Tout
      // refus de catalogue du devis — produit inactif, option inactive, groupe
      // obligatoire sans réponse — tombait donc sur « Une erreur est
      // survenue ». Chez Yuji le 2026-09-02, le serveur a réessayé 9 fois en
      // 50 s puis tâtonné plat par plat pour trouver le coupable.
      const refusal = describeQuoteRefusal(
        err,
        fresh,
        cartLines.map((line) => ({ productId: line.itemId })),
      );
      if (refusal) {
        setErrorText(t(refusal.key, refusal.params));
        return;
      }
      // Anything still unrecognised → generic.
      setErrorText(t('waiter.ticket.errors.generic'));
    } finally {
      if (!cancelledRef.current) setSubmitting(false);
    }
  }

  // SC-902 — design §1.7: unchanged (`confirm.submit`, « Confirmer ») when
  // the choice never appears. « Tout en même temps » always says « Tout
  // envoyer ». By-course names the first `now` group ; when the plan holds
  // or joins EVERY group (no group is earlier than every held service), there
  // is nothing to send right away — « Mettre en attente ».
  const submitLabel = !showCourseChoice
    ? t('waiter.ticket.confirm.submit')
    : sendMode === 'all'
      ? t('waiter.ticket.confirm.submitAll')
      : firstNowEntry
        ? t('waiter.ticket.confirm.submitFirst', { name: entryName(firstNowEntry) })
        : t('waiter.ticket.confirm.submitHold');

  // DF-0557 — the title names the table (or the note), and a table that
  // already sent rounds says so: « Déjà 2 tournées envoyées · ouverte à
  // 13:03 » on what the waiter believes is a fresh table is the cue that the
  // phone reopened the last table served.
  const title =
    ticketKind === 'dine_in'
      ? t('waiter.ticket.confirm.titleTable', { table: ticketLabel })
      : t('waiter.ticket.confirm.titleNote', { name: ticketLabel });
  const priorRounds = sessionDetail?.session.roundCount ?? 0;
  const openedAt = sessionDetail?.session.openedAt;

  return (
    <div
      data-testid="confirm-overlay"
      className="fixed inset-0 z-[1180] flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--w-scrim)' }}
    >
      <div
        data-testid="confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-[360px] max-h-[85dvh] overflow-y-auto waiter-scroll bg-paper rounded-2xl p-5 flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1">
          <h2 data-testid="confirm-title" className="font-sans text-2xl font-bold break-words">
            {title}
          </h2>
          {priorRounds > 0 && openedAt && (
            <p data-testid="confirm-prior-rounds" className="font-sans text-[15px] font-semibold text-ink-mute">
              {t(
                priorRounds === 1 ? 'waiter.ticket.confirm.priorRoundsOne' : 'waiter.ticket.confirm.priorRoundsOther',
                { count: priorRounds, time: formatTicketTime(openedAt) },
              )}
            </p>
          )}
        </div>
        <p className="font-sans text-[18px] font-medium">{t('waiter.ticket.confirm.body')}</p>

        {showCourseChoice && (
          <div data-testid="confirm-send-mode" className="flex flex-col gap-2">
            <span className="font-mono text-sm font-bold">{t('waiter.ticket.confirm.sendMode.title')}</span>
            <div className="flex gap-2" role="radiogroup" aria-label={t('waiter.ticket.confirm.sendMode.title')}>
              <button
                type="button"
                data-testid="confirm-send-by-course"
                role="radio"
                aria-checked={sendMode === 'byCourse'}
                onClick={() => setSendMode('byCourse')}
                className="min-h-[44px] flex-1 border-2 rounded-btn-sm px-3 py-2 font-sans text-left"
                style={{
                  borderColor: 'var(--ink)',
                  backgroundColor: sendMode === 'byCourse' ? 'var(--w-incart-bg)' : 'var(--paper)',
                  color: sendMode === 'byCourse' ? 'var(--w-incart-fg)' : 'var(--ink)',
                }}
              >
                <span className="block text-base font-bold">{t('waiter.ticket.confirm.sendMode.byCourse')}</span>
                <span className="block text-[15px]">{t('waiter.ticket.confirm.sendMode.byCourseHint')}</span>
              </button>
              <button
                type="button"
                data-testid="confirm-send-all"
                role="radio"
                aria-checked={sendMode === 'all'}
                onClick={() => setSendMode('all')}
                className="min-h-[44px] flex-1 border-2 rounded-btn-sm px-3 py-2 font-sans text-left"
                style={{
                  borderColor: 'var(--ink)',
                  backgroundColor: sendMode === 'all' ? 'var(--w-incart-bg)' : 'var(--paper)',
                  color: sendMode === 'all' ? 'var(--w-incart-fg)' : 'var(--ink)',
                }}
              >
                <span className="block text-base font-bold">{t('waiter.ticket.confirm.sendMode.all')}</span>
                <span className="block text-[15px]">{t('waiter.ticket.confirm.sendMode.allHint')}</span>
              </button>
            </div>
            {/* F6 — the recap must agree with the wire: `sendByCourse` is
                exactly the flag that puts `course`/`held_round` on the
                request (see the `postRound` call above), so « Tout
                envoyer » (sendMode === 'all') shows no per-service rows
                rather than a hold/join breakdown the request doesn't send. */}
            {sendByCourse && (
              <div className="flex flex-col gap-1">
                {coursePlan.map((entry) => {
                  const dishCount = entry.lines.reduce((n, l) => n + l.qty, 0);
                  return (
                  <div
                    key={entry.course}
                    data-testid={`confirm-plan-${entry.course}`}
                    className="flex items-center justify-between gap-2 font-sans text-[15px]"
                  >
                    <span className="font-semibold">{entryName(entry)}</span>
                    <span>
                      {/* F4 — project convention (see TicketFooter.tsx
                          withLinesBodyOne/Other, TicketPage.tsx
                          leaveGuard.messageOne/Other): hand-picked key, not
                          i18next's automatic `_one`/`_other` suffix. */}
                      {t(
                        dishCount === 1
                          ? 'waiter.ticket.confirm.plan.dishesOne'
                          : 'waiter.ticket.confirm.plan.dishesOther',
                        { count: dishCount },
                      )}
                    </span>
                    {/* F3 — hold/join is a normal waiting state, not a
                        destructive one: the amber « En attente » pair, never
                        `--w-danger` (reserved for Partir / red −). */}
                    <span style={{ color: entry.kind === 'now' ? 'var(--w-success)' : 'var(--w-pending-fg)' }}>
                      {entry.kind === 'now' && t('waiter.ticket.confirm.plan.now')}
                      {entry.kind === 'hold' && t('waiter.ticket.confirm.plan.hold')}
                      {entry.kind === 'join' && t('waiter.ticket.confirm.plan.join', { name: entry.label })}
                    </span>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {cartOptionGroups.length > 0 && (
          <div data-testid="confirm-cart-options" className="flex flex-col gap-3">
            {cartOptionGroups.map((group) => {
              const selection = answeredCartOptions.find((s) => s.groupId === group.id);
              const required = (group.min ?? 0) >= 1;
              const missing = required && selectedCount(selection) < (group.min ?? 1);
              return (
                <div key={group.id} data-testid={`confirm-cart-option-group-${group.id}`}>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-mono text-sm font-bold">{group.label}</span>
                    <span className="font-sans text-[15px] text-ink-mute">
                      {required ? t('checkout.cartOptions.required') : t('checkout.cartOptions.optional')}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {group.options.map((opt) => {
                      const checked = (selection?.options ?? []).some((o) => o.id === opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          data-testid={`confirm-cart-option-${opt.id}`}
                          aria-pressed={checked}
                          onClick={() =>
                            setCartOptionAnswers((prev) => toggleCartOptionAnswer(prev, group, opt.id))
                          }
                          className="min-h-[44px] border-2 rounded-btn-sm px-3 py-1.5 font-sans text-base"
                          style={{
                            borderColor: 'var(--ink)',
                            backgroundColor: checked ? 'var(--w-incart-bg)' : 'var(--paper)',
                            color: checked ? 'var(--w-incart-fg)' : 'var(--ink)',
                          }}
                        >
                          {opt.label}
                          {opt.priceDelta > 0 ? ` +${formatPrice(opt.priceDelta)}` : ''}
                        </button>
                      );
                    })}
                  </div>
                  {missing && (
                    <p data-testid={`confirm-cart-option-missing-${group.id}`} className="mt-1 font-sans text-[15px]" style={{ color: 'var(--w-danger)' }}>
                      {t('checkout.cartOptions.requiredMissing')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {(discountCents > 0 || cartOptionsSupplement > 0) && (
          <div data-testid="confirm-recap" className="flex flex-col gap-1 font-mono text-[15px] tabular-nums">
            <div className="flex justify-between">
              <span>{t('order.subtotal')}</span>
              <span>{formatPrice(cartTotalCents)}</span>
            </div>
            {discountCents > 0 && (
              <div data-testid="confirm-discount" className="flex justify-between" style={{ color: 'var(--w-success)' }}>
                <span>
                  {promoMatch?.rule.label?.trim() || t('order.discountLabel')} −{promoMatch?.discountPercent} %
                </span>
                <span>−{formatPrice(discountCents)}</span>
              </div>
            )}
            {cartOptionsSupplement > 0 && (
              <div className="flex justify-between">
                <span>{t('checkout.cartOptions.title')}</span>
                <span>+{formatPrice(cartOptionsSupplement)}</span>
              </div>
            )}
            <div data-testid="confirm-total" className="flex justify-between font-bold text-lg">
              <span>{t('order.total')}</span>
              <span>{formatPrice(totalCents)}</span>
            </div>
          </div>
        )}

        {staleLines.length > 0 && (
          <div data-testid="confirm-stale-lines" className="flex flex-col gap-2">
            <p className="font-sans text-[15px] font-semibold" style={{ color: 'var(--w-danger)' }}>
              {t('waiter.ticket.stale.title')}
            </p>
            {staleLines.map((stale) => (
              <div
                key={stale.lineId}
                data-testid={`confirm-stale-line-${stale.lineId}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="min-w-0 font-sans text-[15px]">
                  <span className="font-semibold">{stale.name}</span>
                  {' — '}
                  {staleText(stale)}
                </span>
                {stale.reason === 'optionRules' ? (
                  <button
                    type="button"
                    data-testid={`confirm-stale-complete-${stale.lineId}`}
                    onClick={() => onEditLine(stale.lineId)}
                    className="min-h-[44px] shrink-0 border-2 border-ink rounded-btn-sm px-3 font-sans text-base font-bold"
                  >
                    {t('waiter.ticket.stale.complete')}
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={`confirm-stale-remove-${stale.lineId}`}
                    onClick={() => dispatch(setWaiterLineQty({ sessionUuid, lineId: stale.lineId, qty: 0 }))}
                    className="min-h-[44px] shrink-0 border-2 rounded-btn-sm px-3 font-sans text-base font-bold"
                    style={{ borderColor: 'var(--w-danger)', color: 'var(--w-danger)' }}
                  >
                    {t('waiter.ticket.stale.remove')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {needsStaffPin && (
          <label className="flex flex-col gap-1">
            <span className="font-mono text-sm font-bold">{t('waiter.ticket.confirm.staffPinLabel')}</span>
            <input
              data-testid="confirm-staff-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={12}
              autoFocus
              value={staffPin}
              onChange={(e) => setStaffPin(e.target.value.replace(/\D/g, ''))}
              className="h-12 border-2 border-ink rounded-btn-sm px-3 font-mono text-[18px] tracking-[0.3em]"
            />
          </label>
        )}

        {errorText && (
          <p data-testid="round-error" role="alert" className="font-mono text-xs" style={{ color: 'var(--w-danger)' }}>
            {errorText}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="confirm-cancel"
            onClick={handleClose}
            className="h-12 flex-1 border-2 border-ink rounded-btn font-sans text-[18px] font-bold disabled:opacity-40"
          >
            {t('waiter.ticket.confirm.cancel')}
          </button>
          <button
            type="button"
            data-testid="confirm-submit"
            disabled={submitting || cooldown > 0 || cartLines.length === 0}
            aria-busy={submitting}
            onClick={handleConfirm}
            className="h-12 flex-1 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
            style={{ backgroundColor: 'var(--w-success)' }}
          >
            {submitting ? t('waiter.notes.online.actionPending') : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
